import React, { useState } from 'react';

import get from 'lodash.get';
import VisibilitySensor from 'react-visibility-sensor';
import { Col, Row } from 'reactstrap';
import styled, { css } from 'styled-components';

import checkMark from 'assets/checkmark.svg';
import dashBoardIcon from 'components/ModularBlocks/TwoBlockContent/components/Icons/dashboard-icon.svg';
import forexIcon from 'components/ModularBlocks/TwoBlockContent/components/Icons/forex-icon.svg';
import promotionsIcon from 'components/ModularBlocks/TwoBlockContent/components/Icons/promotions-icon.svg';

import CtaButton from 'components/CtaButton/CtaButton';
import Flex from 'components/Flex/Flex';
import InnerHtml from 'components/InnerHtml/InnerHtml';
import BlockStyle from 'components/ModularBlocks/BlockStyle';
import BlockImage from 'components/ModularBlocks/TwoBlockContent/components/BlockImage';

import { SingleBlockShape } from 'interfaces/content-stack/modular-blocks/twoBlockContent';

import { CONTENT_ALIGN, MOBILE_CONTENT_ALIGN } from 'utils/constants';
import { autoOptimizeImage, findVideoExtension } from 'utils/functions';
import { getModularBlockLink } from 'utils/modularBlockUtils';

const iconMapping: { [key: string]: string } = {
  promotionsIcon: promotionsIcon,
  dashboardIcon: dashBoardIcon,
  forexIcon: forexIcon,
  checkMark: checkMark,
};

interface StyledListItem {
  listItem: string;
  listIcon: string;
}

type BlockContentProps = {
  $bkgdImage?: string | undefined;
  $bkgdColor?: string | undefined;
  $contentAlign?: string | undefined;
  $mobileContentAlign?: string | undefined;
  $videoExt?: string | undefined;
  $hasButtonBackground?: boolean;
};

const BlockContent = styled('div')<BlockContentProps>(
  ({ $bkgdImage, $contentAlign, $mobileContentAlign }) => css`
    display: flex;
    flex-direction: column;
    height: 100%;
    align-items: center;

    ${$bkgdImage &&
    css`
      background-image: url(${$bkgdImage});
      background-size: cover;
      height: 100%;
    `}

    ${$contentAlign &&
    css`
      justify-content: ${$contentAlign};
    `}

      ${$mobileContentAlign &&
    css`
      @media screen and (max-width: 991px) {
        text-align: center;
      }
    `}
  `
);

const BlockVideo = styled.video<{ $overflow?: boolean; $boxShadow?: boolean }>`
  ${({ $overflow, theme }) =>
    $overflow
      ? `
    @media (max-width: ${theme.breakpoints.lg}) {
      max-width: 100%;
      max-height: 100%;
    }
    `
      : `
    max-width: 100%;
    max-height: 100%;
    `}

  box-shadow: ${(props) =>
    props.$boxShadow ? `0 .5rem 1.25rem 0.375rem ${props.theme.colors.secondaryBoxShadowGray}` : 'none'};
`;

const BlockVideoContainer = styled.div<{ $displayed?: boolean; $applyTransition?: boolean }>`
  ${({ $displayed, $applyTransition }) =>
    $applyTransition &&
    `
      opacity: 0;
      transform: translateX(20%);
      transition: opacity 1s ease, transform 1s ease-out;

      ${
        $displayed &&
        `
          opacity: 1;
          transform: translateX(0);
        `
      }
    `}
`;

const BlockText = styled('p')(
  ({ theme }) => css`
    font-family: ${theme.fonts.default};
    font-size: 1.25rem;
    font-weight: 500;
  `
);

const ImageTextContainer = styled.div`
  display: flex;
  flex-direction: column-reverse;
`;

const Text = styled('p')<{ $bodyDisclaimerColor?: string | undefined }>(
  ({ theme }) => css`
    color: ${theme.colors.lightGray};
    font-weight: 500;
  `
);

const ListContainer = styled.div`
  gap: 1rem;
  flex-flow: column;
  display: flex;
`;

const Title = styled.h3`
  color: ${(props) => props.theme.colors.white};
  text-transform: uppercase;
  font-weight: 600;
  text-align: right;
  white-space: nowrap;
`;

const ItemListContainer = styled.div`
  grid-column-gap: 1rem;
  grid-row-gap: 0.5rem;
  grid-template-rows: auto;
  grid-template-columns: 1.5rem 1fr;
  display: flex;
  align-items: center;
`;

const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const ListItem = styled.p`
  font-weight: 500;
  margin-bottom: 0;
`;

const Image = styled.img`
  width: 4rem;
`;

const StyledIcon = styled.img`
  height: 1.25rem;
  width: 1.25rem;
`;

const BlockHeader = styled('h2')(
  ({ theme }) => css`
    padding-top: 1.5rem;
    text-transform: uppercase;
    font-family: ${theme.fonts.fontFamilySecondary};
    font-size: 3rem;
    font-weight: 500;

    @media (max-width: ${(props) => props.theme.breakpoints.lg}) {
      font-size: 2.75rem;
      padding-top: 0;
    }

    @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
      font-size: 2rem;
    }
  `
);

const BlockRichText = styled.div`
  ${({ theme }) => `
  p {
    font-size: 1.125rem;
    
    @media (max-width: ${theme.breakpoints.md}) {
      font-size: 0.875rem;
    }
  }
  `}
`;

const ButtonsContainer = styled('div')<{ $hasButtonBackground?: boolean }>(
  ({ theme, $hasButtonBackground }) => css`
    display: flex;
    width: 100%;
    gap: 1.5rem;
    align-items: center;
    white-space: nowrap;

    @media (max-width: ${theme.breakpoints.xs}) {
      flex-direction: ${$hasButtonBackground ? 'row' : 'column'};
    }
  `
);

const BlockContainer = styled.div<{ $overflow?: boolean }>`
  display: flex;
  flex-direction: column;
  padding: 0;
  flex-wrap: nowrap;
  align-items: flex-start;
  justify-content: flex-start;
  width: 100%;
  height: auto;
  gap: 1.5rem;

  ${({ $overflow, theme }) =>
    $overflow &&
    `
    align-self: flex-start;

    @media (max-width: ${theme.breakpoints.lg}) {
      align-self: center;
    }
    `}
`;

type Props = {
  contentAlign?: string | undefined;
  mobileContentAlign?: string | undefined;
  data: SingleBlockShape;
};

const Block: React.FC<Props> = ({ contentAlign, mobileContentAlign, data }) => {
  const {
    singleLine,
    body,
    header,
    background = { backgroundImage: undefined, backgroundColor: undefined },
    blockImage,
    blockVideo,
    optionalCta,
    blockDisclaimer,
    bodyDisclaimerColor,
    twoColumn,
    fullHeader,
    blockStyle,
  } = data;
  const [videoDisplayed, setVideoDisplayed] = useState(false);
  const { backgroundColor, backgroundImage } = background;
  const labelImageText = twoColumn?.labelImageText ?? undefined;
  const optimizedBackground = autoOptimizeImage(get(backgroundImage, 'url'));
  const hasButtonBackground = optionalCta?.some((cta) => cta.buttonBackground?.url);

  function handleVideoDisplay(isVisible: boolean) {
    if (isVisible && !videoDisplayed) {
      setVideoDisplayed(true);
    }
  }

  return (
    <BlockStyle blockStyle={blockStyle}>
      <BlockContent
        $bkgdColor={backgroundColor}
        $bkgdImage={optimizedBackground}
        $contentAlign={CONTENT_ALIGN[contentAlign as keyof typeof CONTENT_ALIGN]}
        $mobileContentAlign={MOBILE_CONTENT_ALIGN[mobileContentAlign as keyof typeof MOBILE_CONTENT_ALIGN]}
      >
        <BlockContainer $overflow={!!blockVideo?.overflowVideo}>
          <Flex column gap={1.5}>
            {header && <BlockHeader>{header}</BlockHeader>}
            {fullHeader && (
              <BlockRichText>
                <InnerHtml richText={fullHeader} fontWeight='500' />
              </BlockRichText>
            )}
            {singleLine && <BlockText>{singleLine}</BlockText>}
            {body && (
              <BlockRichText>
                <InnerHtml richText={body} fontWeight='400' />
              </BlockRichText>
            )}
            <ListContainer>
              {data.styledList &&
                Array.isArray(data.styledList) &&
                data.styledList.map((item: StyledListItem, index: number) => (
                  <React.Fragment key={index}>
                    <ItemListContainer>
                      {iconMapping[item.listIcon] && (
                        <StyledIcon src={iconMapping[item.listIcon]} alt={item.listIcon} />
                      )}
                      <ListItem>{item.listItem}</ListItem>
                    </ItemListContainer>
                  </React.Fragment>
                ))}
            </ListContainer>
          </Flex>
          {blockVideo?.video ? (
            <VisibilitySensor onChange={handleVideoDisplay} partialVisibility={true}>
              <BlockVideoContainer $displayed={videoDisplayed} $applyTransition={blockVideo?.applyTransition}>
                <BlockVideo
                  autoPlay={true}
                  loop={true}
                  muted={true}
                  playsInline={true}
                  $overflow={blockVideo.overflowVideo}
                  $boxShadow={blockVideo?.applyDropshadow}
                >
                  <source src={blockVideo.video.url} type={`video/${findVideoExtension(blockVideo.video)}`} />
                </BlockVideo>
              </BlockVideoContainer>
            </VisibilitySensor>
          ) : (
            <BlockImage
              altText={blockImage?.imageAltText}
              images={blockImage?.image}
              boxShadow={blockImage?.applyDropshadow}
            />
          )}
          <ButtonsContainer $hasButtonBackground={hasButtonBackground}>
            {optionalCta?.map((cta, index) => (
              <CtaButton
                to={getModularBlockLink(cta.buttonLink)}
                openInNewWindow={cta.buttonLink[0]?.openInNewWindow}
                key={index}
                buttonType={cta.buttonType}
                bold
                text={cta?.buttonText}
                backgroundImage={cta.buttonBackground?.url}
                dataLayerInfo={header}
                aria-label={cta.ariaLabel}
              />
            ))}
          </ButtonsContainer>
          {blockDisclaimer && <InnerHtml richText={blockDisclaimer} textColor={bodyDisclaimerColor} />}
          {labelImageText?.map((item) => {
            return (
              <div key={item.title}>
                <Row>
                  <Col xs={6}>
                    <TitleContainer>
                      <Title>{item.title}</Title>
                    </TitleContainer>
                  </Col>
                  <Col xs={6}>
                    <ImageTextContainer>
                      <Text>{item.text}</Text>
                      <Image src={item?.image?.url} alt={item.imageAltText ?? 'Image'} />
                    </ImageTextContainer>
                  </Col>
                </Row>
              </div>
            );
          })}
        </BlockContainer>
      </BlockContent>
    </BlockStyle>
  );
};

export default Block;
